/**
 * PrintContext — global "browser-print anywhere" pipeline.
 *
 * Motivation: Kitchen Bridge was flaky and won't install for some
 * users. Rather than route every print through a LAN bridge or the
 * cloud thermal API, we hand the sticker straight to the browser's
 * native print dialog — the same trick the main Label Printer page
 * has always used (window.print() on the current page + print-CSS to
 * hide everything except the sticker preview).
 *
 * How it works
 * ------------
 *   1. Any component calls `usePrinter().printLabel(previewData)` OR
 *      `.printEquipmentStickers({ items, siteName })`.
 *   2. This context stores the payload and mounts <PrintOverlay />
 *      into the DOM inside a `.print-area` container.
 *   3. An effect fires `window.print()` on the next tick, giving
 *      React time to commit the DOM (barcode SVGs need a paint).
 *   4. Global print-CSS (index.css `@media print`) hides everything
 *      except `.print-area`, so only the sticker(s) reach the paper.
 *   5. Once the dialog closes we clear the payload so the overlay
 *      disappears again.
 *
 * No popups, no `window.open`, no LAN bridge — just a first-party
 * `window.print()` from a real user gesture. Browsers universally
 * allow this.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import LabelPreview from "@/components/labels/LabelPreview";
import EquipmentBarcodeSticker, { BarcodeSvg } from "@/components/EquipmentBarcodeSticker";
import RecipeCard from "@/components/RecipeCard";
import WorkflowLabelPreview from "@/components/workflow/WorkflowLabelPreview";

const PrintContext = createContext(null);

export function usePrinter() {
  const ctx = useContext(PrintContext);
  if (!ctx) {
    // Never fatal — a component outside the provider just gets a
    // no-op printer so unit tests / storybook don't blow up.
    return { printLabel: () => {}, printEquipmentStickers: () => {}, printRecipeCards: () => {}, printWorkflowLabel: () => {} };
  }
  return ctx;
}

export function PrintProvider({ children }) {
  // payload.kind === "label" | "equipment" | "recipe" | null
  const [payload, setPayload] = useState(null);

  // Dynamic @page override — Chromium's `page.pdf(preferCSSPageSize)`
  // and the browser's native print dialog both honour a runtime
  // <style>@page{size:WxH}</style>. Setting it per-print lets a site
  // with fixed 60×50mm die-cut stickers force a page break at 50mm
  // exactly, so long labels split onto the next sticker instead of
  // rendering as one tall 300mm-long page (iter32 finding).
  useEffect(() => {
    const el = document.createElement("style");
    el.id = "print-page-override";
    document.head.appendChild(el);
    return () => el.remove();
  }, []);
  useEffect(() => {
    const el = document.getElementById("print-page-override");
    if (!el) return;
    if (payload?.kind === "label" && payload.widthMm && payload.heightMm) {
      // Landscape site labels are just swapped mm; we already handle
      // that upstream by passing swapped widthMm/heightMm.
      el.textContent = `@page { size: ${payload.widthMm}mm ${payload.heightMm}mm; margin: 2mm; }`;
    } else if (payload?.kind === "workflow" && payload.widthMm && payload.heightMm) {
      // 60×50 mm Tecor stickers — force the page break at the roll size
      // so a long retail label splits onto the next sticker cleanly.
      el.textContent = `@page { size: ${payload.widthMm}mm ${payload.heightMm}mm; margin: 2mm; }`;
    } else if (payload?.kind === "recipe") {
      el.textContent = ""; // recipe uses the named recipe-page in index.css
    } else {
      el.textContent = "";
    }
  }, [payload]);

  const printLabel = useCallback((preview, opts = {}) => {
    setPayload({
      kind: "label",
      label: preview,
      widthMm: opts.widthMm,
      heightMm: opts.heightMm,
    });
  }, []);

  const printEquipmentStickers = useCallback(({ items, siteName }) => {
    if (!items?.length) return;
    setPayload({ kind: "equipment", items, siteName: siteName || "" });
  }, []);

  /**
   * Print one or more recipe cards (A4). Migrated off `window.open`
   * popup so it survives browsers with strict popup blockers — the
   * card is rendered inside the same PrintOverlay portal used for
   * food labels and equipment stickers.
   */
  const printRecipeCards = useCallback(({ recipes, siteName }) => {
    const list = Array.isArray(recipes) ? recipes : [recipes];
    if (!list.length) return;
    setPayload({ kind: "recipe", recipes: list, siteName: siteName || "" });
  }, []);

  /** Browser-print a 60×50 mm Prep/Retail workflow label (fallback for
   * when the Tecor isn't connected or WebUSB is unsupported). */
  const printWorkflowLabel = useCallback((preview, opts = {}) => {
    if (!preview) return;
    setPayload({
      kind: "workflow",
      preview,
      widthMm: opts.widthMm || 60,
      heightMm: opts.heightMm || 50,
    });
  }, []);

  // Fire the OS print dialog on the next paint tick so the barcode
  // SVGs (rendered by JsBarcode in an effect) are committed before
  // the browser rasterises for print.
  useEffect(() => {
    if (!payload) return;
    // Toggle a `html.is-printing` class so print CSS can hide the
    // rest of the app without needing the `:has()` selector (which
    // some older browsers don't support reliably). Class is applied
    // BEFORE the first paint tick so the browser's live print
    // preview sees the overlay-only DOM from the start.
    const root = document.documentElement;
    root.classList.add("is-printing");

    // requestAnimationFrame gives us a solid "layout done, ready to
    // paint" hook — 100 ms setTimeout was flaky on slower tablets.
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          window.print();
        } finally {
          const clear = () => {
            root.classList.remove("is-printing");
            setPayload(null);
          };
          // afterprint fires reliably in Chromium; safety-net setTimeout
          // covers Safari/Firefox where the event sometimes doesn't.
          window.addEventListener("afterprint", clear, { once: true });
          setTimeout(clear, 2500);
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf);
      root.classList.remove("is-printing");
    };
  }, [payload]);

  return (
    <PrintContext.Provider value={useMemo(
      () => ({ printLabel, printEquipmentStickers, printRecipeCards, printWorkflowLabel }),
      [printLabel, printEquipmentStickers, printRecipeCards, printWorkflowLabel],
    )}>
      {children}
      <PrintOverlay payload={payload} />
    </PrintContext.Provider>
  );
}

/**
 * The DOM the browser actually rasterises. Portalled to document.body
 * as a direct sibling of #root so print CSS can hide the entire React
 * app tree (`#root { display: none }`) and only show this overlay
 * (`.print-overlay-root { display: block }`) — without breaking any
 * page that renders its own inline `.print-area` (e.g. LabelPrinter).
 * Screen-hidden via `display: none` in normal CSS; unveiled only
 * inside `@media print`.
 */
function PrintOverlay({ payload }) {
  if (!payload) return null;
  if (typeof document === "undefined") return null;

  const content = payload.kind === "label" ? (
    <div className="print-area" data-testid="print-overlay-label">
      <LabelPreview
        label={payload.label}
        realSize={Boolean(payload.widthMm)}
        widthMm={payload.widthMm}
        heightMm={payload.heightMm}
      />
      {/*
        Second sticker: batch-number Code128 barcode. Prints on its
        own page (page-break-before) so a two-sticker roll advances
        automatically to the next sticker. Only rendered when the
        label carries a REAL batch — LabelPrinter emits '—' (em-dash
        U+2014) as its placeholder fallback and '-' when explicitly
        cleared; guard against both so a blank batch doesn't produce
        a barcode-of-dashes sticker.
      */}
      {payload.label?.batch
        && payload.label.batch !== "-"
        && payload.label.batch !== "—"
        && payload.label.batch.trim() !== ""
      ? (
        <div
          className="print-area label-barcode-sheet"
          data-testid="print-overlay-label-barcode"
          style={{
            pageBreakBefore: "always",
            breakBefore: "page",
            textAlign: "center",
            padding: "6mm 4mm",
            background: "#fff",
            color: "#000",
          }}
        >
          <div style={{
            fontFamily: "'Courier New', monospace",
            fontSize: "10px",
            letterSpacing: "2px",
            textTransform: "uppercase",
            marginBottom: "4mm",
          }}>
            {(payload.label.product || "").toUpperCase()}
          </div>
          <BarcodeSvg
            value={payload.label.batch}
            height={70}
            fontSize={14}
          />
        </div>
      ) : null}
    </div>
  ) : payload.kind === "workflow" ? (
    <div className="print-area" data-testid="print-overlay-workflow">
      <WorkflowLabelPreview preview={payload.preview} realSize />
    </div>
  ) : payload.kind === "recipe" ? (
    // Recipe cards use their own `.recipe-card` class (paired with a
    // named @page for A4). No `.print-area` wrapper needed — the
    // html.is-printing class already hides the app tree.
    <div data-testid="print-overlay-recipe">
      {payload.recipes.map((r) => (
        <RecipeCard key={r.id || r.name} recipe={r} siteName={payload.siteName} />
      ))}
    </div>
  ) : (
    // Equipment — one .print-area wraps N stickers so the @media print
    // reveal captures all of them at once. Inline max-width lets 2-up
    // A4 sheets flow while a 60mm sticker roll still prints single
    // stickers per page.
    <div
      className="print-area flex flex-wrap gap-4"
      data-testid="print-overlay-equipment"
      style={{ width: "auto", maxWidth: "190mm" }}
    >
      {payload.items.map((eq) => (
        <EquipmentBarcodeSticker
          key={eq.id}
          equipment={eq}
          siteName={payload.siteName}
        />
      ))}
    </div>
  );

  return createPortal(
    <div className="print-overlay-root" aria-hidden>
      {content}
    </div>,
    document.body,
  );
}
